import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const FALLBACK_SITE_URL = 'https://tradingspace.online';
const SITE_NAME = 'TradingSpace';
const DEFAULT_DESCRIPTION =
  'TradingSpace turns trading history into clearer decisions with trade import, dashboard analytics, diary notes, AI reports and a focused beta workspace for traders.';

export type SeoJsonLd = Record<string, unknown> | Array<Record<string, unknown>>;

interface SeoProps {
  title?: string;
  description?: string;
  path?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  locale?: 'it' | 'en';
  noIndex?: boolean;
  jsonLd?: SeoJsonLd;
}

export const getPublicSiteUrl = (): string => {
  const configured = process.env.REACT_APP_PUBLIC_SITE_URL || FALLBACK_SITE_URL;
  return configured.replace(/\/+$/, '');
};

export const absoluteUrl = (pathOrUrl?: string): string => {
  const siteUrl = getPublicSiteUrl();
  if (!pathOrUrl) return siteUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${siteUrl}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
};

export const getCanonicalUrl = (path = '/'): string => absoluteUrl(path.split('#')[0].split('?')[0] || '/');

const normalizeTitle = (title?: string): string => {
  if (!title) return `${SITE_NAME} | Trading data into decisions`;
  return title.toLowerCase().includes('tradingspace') ? title : `${title} | ${SITE_NAME}`;
};

const normalizeDescription = (description?: string): string => {
  const value = (description || DEFAULT_DESCRIPTION).trim();
  return value.length > 170 ? `${value.slice(0, 167).trim()}...` : value;
};

const jsonLdArray = (jsonLd?: SeoJsonLd): Array<Record<string, unknown>> => {
  if (!jsonLd) return [];
  return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
};

const SEO_DEDUPE_SELECTORS = [
  'title',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[property="og:site_name"]',
  'meta[property="og:type"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:image"]',
  'meta[property="og:locale"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
  'link[rel="canonical"]',
  'link[rel="alternate"][hreflang="x-default"]'
];

/**
 * Snapshot of the SEO tags that ship in the static index.html, taken at module
 * load — i.e. BEFORE React mounts and before react-helmet-async injects
 * anything. Everything in here is safe to delete; everything else belongs to
 * React and must never be removed by hand.
 *
 * Why this matters: this version of react-helmet-async does NOT tag its nodes
 * with `data-rh`, and it PREPENDS them. The old "keep the last one" cleanup was
 * therefore deleting Helmet's own tag, which (a) left every page on the generic
 * title and (b) left React holding a node with no parent — the
 * "Cannot read properties of null (reading 'removeChild')" crash on unmount.
 */
const STATIC_HEAD_TAGS: Set<Element> = (() => {
  const set = new Set<Element>();
  if (typeof document === 'undefined') return set;
  SEO_DEDUPE_SELECTORS.forEach((selector) => {
    document.head.querySelectorAll(selector).forEach((el) => set.add(el));
  });
  return set;
})();

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path = '/',
  canonical,
  image,
  type = 'website',
  locale = 'en',
  noIndex = false,
  jsonLd
}) => {
  const normalizedTitle = normalizeTitle(title);
  const normalizedDescription = normalizeDescription(description);
  const canonicalUrl = canonical || getCanonicalUrl(path);
  const imageUrl = absoluteUrl(image || '/og-default.png?v=2');
  const robots = noIndex ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large';
  const ogLocale = locale === 'it' ? 'it_IT' : 'en_US';

  useEffect(() => {
    // The static CRA template contains safe fallback meta tags for first paint.
    // Once Helmet renders route-specific SEO, remove those fallbacks to avoid
    // duplicate canonical/description tags on blog posts and legal pages.
    SEO_DEDUPE_SELECTORS.forEach((selector) => {
      const elements = Array.from(document.head.querySelectorAll(selector));
      if (elements.length <= 1) return;

      // Only ever delete tags that came from the static index.html, and only
      // once React has rendered its own replacement for that selector.
      // Removing a React-owned node is what caused the removeChild crash.
      const reactOwned = elements.filter((el) => !STATIC_HEAD_TAGS.has(el));
      if (reactOwned.length === 0) return;

      elements.forEach((element) => {
        if (STATIC_HEAD_TAGS.has(element)) {
          element.remove();
          STATIC_HEAD_TAGS.delete(element);
        }
      });
    });

    // The static <title> from index.html is not Helmet-managed and must not be
    // ripped out of the DOM (same crash). Setting textContent is safe: the node
    // stays where React/Helmet expect it.
    if (normalizedTitle && document.title !== normalizedTitle) {
      document.title = normalizedTitle;
    }
  }, [normalizedTitle, normalizedDescription, canonicalUrl, imageUrl, robots, ogLocale, type]);

  return (
    <Helmet htmlAttributes={{ lang: locale }}>
      <title>{normalizedTitle}</title>
      <meta name="description" content={normalizedDescription} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={normalizedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={normalizedDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLdArray(jsonLd).map((entry, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
